import React, { useCallback, useEffect, useState } from 'react'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Card, Collapse, Input, Layout, Modal, notification, PageHeader, Select, Space, Table, Typography } from 'antd'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { initializePages } from '~actions'
import { concessionFiltersEndpoint } from '~apiConfig'
import { FLAGS_COLOR_CODES } from '~constants'
import { getCurrentUser } from '~context'
import { Spinner } from '~stories'
import { getApiResponseInJSON, getIdToken } from '~utils'
import {
  getConcessionFiltersAdminFieldErrors,
  hasConcessionFilterCategoryFieldErrors,
  hasConcessionLocationCategoryFieldErrors,
  resolveConcessionLocationTitle,
  validateConcessionFiltersAdmin
} from '~utils/concessionFiltersAdminValidation'
import { toHyphenatedSlug } from '~utils/faqSlugFromVenueName'
import { resolveFlagColorFromInput } from '~utils/flagColor'

const { Panel } = Collapse
const PAGE_TITLE = 'Concession Filters'

const FILTER_COLOR_OPTIONS = FLAGS_COLOR_CODES.map(({ name, value }) => ({
  value,
  label: `${name} (${value})`
}))

const DEFAULT_LOCATION = {
  key: 'location',
  title: 'Location',
  placeholder: 'All Levels',
  sections: []
}

const DEFAULT_CUISINE = {
  key: 'cuisine',
  title: 'Cuisine',
  placeholder: 'All Cuisines',
  options: []
}

const DEFAULT_DIETARY = {
  key: 'dietary',
  title: 'Dietary Preferences',
  placeholder: 'No Dietary Preferences',
  options: []
}

const createRowId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

const resolveLocationTitle = resolveConcessionLocationTitle

const stripFilterItem = ({ _rowId, ...item }) => ({
  ...item,
  key: item.key || toHyphenatedSlug(item.label)
})

const withRowIds = items =>
  (items || []).map(item => {
    const color =
      item.color && typeof item.color === 'object' && !Array.isArray(item.color)
        ? item.color
        : item.flags && typeof item.flags === 'object' && !Array.isArray(item.flags)
          ? item.flags
          : {}

    return {
      ...item,
      color,
      _rowId: item._rowId || createRowId()
    }
  })

const stripLocationItem = ({ _rowId, sections = [], title, label, key }) => ({
  title: resolveLocationTitle({ title, label, key }),
  key: key || toHyphenatedSlug(resolveLocationTitle({ title, label, key })),
  sections: (sections || []).map(stripLocationSubsection)
})

const stripLocationSubsection = ({ _rowId, ...item }) => ({
  ...item,
  label: String(item.label ?? '').trim(),
  key: item.key || toHyphenatedSlug(item.label),
  is_active: item.is_active !== false
})

const withLocationRowIds = items =>
  (items || []).map(item => {
    const title = resolveLocationTitle(item)
    return {
      ...item,
      title,
      key: item.key || toHyphenatedSlug(title),
      _rowId: item._rowId || createRowId(),
      sections: withSubsectionRowIds(item.sections)
    }
  })

const withSubsectionRowIds = items =>
  (items || []).map(item => ({
    ...item,
    _rowId: item._rowId || createRowId()
  }))

const LocationSubsectionsTable = ({ itemErrors = [], items, onChange }) => {
  const updateLabel = (rowId, value) => {
    onChange(currentSections =>
      currentSections.map(item => {
        if (item._rowId !== rowId) return item
        return {
          ...item,
          label: value,
          key: toHyphenatedSlug(value)
        }
      })
    )
  }

  const addItem = () => {
    onChange(currentSections => [
      ...currentSections,
      {
        _rowId: createRowId(),
        key: '',
        label: '',
        is_active: true
      }
    ])
  }

  const removeItem = rowId => {
    onChange(currentSections => currentSections.filter(item => item._rowId !== rowId))
  }

  const columns = [
    {
      title: 'Section',
      dataIndex: 'label',
      render: (_, record, index) => {
        const labelError = itemErrors[index]?.label

        return (
          <Input
            onChange={e => updateLabel(record._rowId, e.target.value)}
            placeholder="Section label"
            status={labelError ? 'error' : undefined}
            value={record.label}
          />
        )
      }
    },
    {
      title: '',
      width: 64,
      align: 'right',
      render: (_, record) => (
        <Button
          aria-label="Delete section row"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(record._rowId)}
          type="text"
        />
      )
    }
  ]

  return (
    <>
      <Table
        columns={columns}
        dataSource={items}
        pagination={false}
        rowKey="_rowId"
        size="small"
      />
      <Button icon={<PlusOutlined />} onClick={addItem} style={{ marginTop: 12 }} type="dashed">
        Add Section
      </Button>
    </>
  )
}

LocationSubsectionsTable.propTypes = {
  itemErrors: PropTypes.array,
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
}

const LocationFilterEditor = ({ categoryError = '', itemErrors = [], items, onChange }) => {
  const [activeLocationKey, setActiveLocationKey] = useState(null)

  const updateTitle = (rowId, value) => {
    onChange(prev =>
      prev.map(item => {
        if (item._rowId !== rowId) return item
        return {
          ...item,
          title: value,
          key: toHyphenatedSlug(value)
        }
      })
    )
  }

  const updateSections = (rowId, sectionsUpdater) => {
    onChange(prev =>
      prev.map(item => {
        if (item._rowId !== rowId) return item
        const currentSections = item.sections || []
        const nextSections =
          typeof sectionsUpdater === 'function'
            ? sectionsUpdater(currentSections)
            : sectionsUpdater
        return { ...item, sections: nextSections }
      })
    )
  }

  const addLocation = () => {
    const rowId = createRowId()
    onChange(prev => [
      ...prev,
      {
        _rowId: rowId,
        title: '',
        key: '',
        sections: []
      }
    ])
    setActiveLocationKey(rowId)
  }

  const removeLocation = rowId => {
    onChange(prev => prev.filter(item => item._rowId !== rowId))
    setActiveLocationKey(current => (current === rowId ? null : current))
  }

  const getLocationHeader = location =>
    resolveLocationTitle(location) || 'New location'

  return (
    <>
      {categoryError && (
        <Typography.Text style={{ display: 'block', marginBottom: 12 }} type="danger">
          {categoryError}
        </Typography.Text>
      )}
      <Collapse
        accordion
        activeKey={activeLocationKey}
        expandIconPosition="right"
        onChange={key => setActiveLocationKey(key || null)}
      >
        {items.map((location, index) => {
          const locationItemErrors = itemErrors[index] || {}

          return (
            <Panel
              key={location._rowId}
              extra={(
                <Button
                  aria-label="Delete location row"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={event => {
                    event.stopPropagation()
                    removeLocation(location._rowId)
                  }}
                  type="text"
                />
              )}
              header={getLocationHeader(location)}
            >
              <div style={{ maxWidth: 520 }}>
                <Typography.Text strong>Title</Typography.Text>
                <Input
                  onChange={e => updateTitle(location._rowId, e.target.value)}
                  placeholder="Display title"
                  status={locationItemErrors.title ? 'error' : undefined}
                  style={{ marginTop: 8 }}
                  value={location.title || ''}
                />
                {locationItemErrors.title && (
                  <Typography.Text style={{ display: 'block', marginTop: 4 }} type="danger">
                    {locationItemErrors.title}
                  </Typography.Text>
                )}
              </div>
              <div style={{ marginTop: 16 }}>
                <LocationSubsectionsTable
                  itemErrors={locationItemErrors.sections || []}
                  items={location.sections || []}
                  onChange={sectionsUpdater => updateSections(location._rowId, sectionsUpdater)}
                />
              </div>
            </Panel>
          )
        })}
      </Collapse>
      <Button icon={<PlusOutlined />} onClick={addLocation} style={{ marginTop: 12 }} type="dashed">
        Add Location
      </Button>
    </>
  )
}

LocationFilterEditor.propTypes = {
  categoryError: PropTypes.string,
  itemErrors: PropTypes.array,
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
}

const FilterOptionsTable = ({ categoryError = '', itemErrors = [], items, onChange, sectionLabel }) => {
  const updateLabel = (rowId, value) => {
    onChange(items.map(item => {
      if (item._rowId !== rowId) return item
      return {
        ...item,
        label: value,
        key: toHyphenatedSlug(value)
      }
    }))
  }

  const updateColor = (rowId, value) => {
    onChange(items.map(item =>
      item._rowId === rowId
        ? { ...item, color: value ? (resolveFlagColorFromInput(value) || {}) : {} }
        : item
    ))
  }

  const addItem = () => {
    onChange([
      ...items,
      {
        _rowId: createRowId(),
        key: '',
        label: '',
        color: {},
        is_active: true
      }
    ])
  }

  const removeItem = rowId => {
    onChange(items.filter(item => item._rowId !== rowId))
  }

  const confirmRemoveItem = record => {
    const itemName = record.label?.trim() || record.key?.trim() || 'this item'
    Modal.confirm({
      title: `Delete ${sectionLabel}?`,
      content: `Are you sure you want to delete "${itemName}"? This change will apply when you save.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => removeItem(record._rowId)
    })
  }

  const columns = [
    {
      title: 'Title',
      dataIndex: 'label',
      width: '35%',
      render: (_, record, index) => {
        const labelError = itemErrors[index]?.label

        return (
          <Input
            onChange={e => updateLabel(record._rowId, e.target.value)}
            placeholder="Display title"
            status={labelError ? 'error' : undefined}
            style={{ width: '100%' }}
            value={record.label}
          />
        )
      }
    },
    {
      title: 'Color',
      width: '35%',
      render: (_, record, index) => {
        const colorError = itemErrors[index]?.color
        const selectedValue = record.color?.value
        const hasPresetColor = !selectedValue || FILTER_COLOR_OPTIONS.some(
          option => option.value.toLowerCase() === selectedValue.toLowerCase()
        )
        const colorOptions = hasPresetColor
          ? FILTER_COLOR_OPTIONS
          : [{
            value: selectedValue,
            label: record.color?.name
              ? `${record.color.name} (${selectedValue})`
              : selectedValue
          }, ...FILTER_COLOR_OPTIONS]

        return (
          <Select
            allowClear
            aria-label={`${sectionLabel} color`}
            onChange={value => updateColor(record._rowId, value)}
            options={colorOptions}
            placeholder="Select color"
            status={colorError ? 'error' : undefined}
            style={{ width: '100%', minWidth: 220 }}
            value={selectedValue || undefined}
          />
        )
      }
    },
    {
      title: '',
      width: 64,
      align: 'right',
      render: (_, record) => (
        <Button
          aria-label={`Delete ${sectionLabel} row`}
          danger
          icon={<DeleteOutlined />}
          onClick={() => confirmRemoveItem(record)}
          type="text"
        />
      )
    }
  ]

  return (
    <>
      {categoryError && (
        <Typography.Text style={{ display: 'block', marginBottom: 12 }} type="danger">
          {categoryError}
        </Typography.Text>
      )}
      <Table
        columns={columns}
        dataSource={items}
        pagination={false}
        rowKey="_rowId"
        size="small"
        style={{ width: '100%' }}
        tableLayout="fixed"
      />
      <Button icon={<PlusOutlined />} onClick={addItem} style={{ marginTop: 12 }} type="dashed">
        Add {sectionLabel}
      </Button>
    </>
  )
}

FilterOptionsTable.propTypes = {
  categoryError: PropTypes.string,
  itemErrors: PropTypes.array,
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  sectionLabel: PropTypes.string.isRequired
}

const FilterPlaceholderField = ({ label, onChange, value }) => (
  <div style={{ marginBottom: 16, maxWidth: 520 }}>
    <Typography.Text strong>{label}</Typography.Text>
    <Input
      onChange={event => onChange(event.target.value)}
      placeholder="Dropdown placeholder text"
      style={{ marginTop: 8 }}
      value={value}
    />
  </div>
)

FilterPlaceholderField.propTypes = {
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired
}

const ConcessionFiltersAdmin = ({ initializePages, venues }) => {
  const { access_token: accessToken, userDetails: { isContentEditor } } = getCurrentUser()
  const [selectedVenueId, setSelectedVenueId] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [locationSections, setLocationSections] = useState([])
  const [locationPlaceholder, setLocationPlaceholder] = useState(DEFAULT_LOCATION.placeholder)
  const [cuisineOptions, setCuisineOptions] = useState([])
  const [cuisinePlaceholder, setCuisinePlaceholder] = useState(DEFAULT_CUISINE.placeholder)
  const [dietaryOptions, setDietaryOptions] = useState([])
  const [dietaryPlaceholder, setDietaryPlaceholder] = useState(DEFAULT_DIETARY.placeholder)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [activeFilterPanels, setActiveFilterPanels] = useState(['location', 'cuisine', 'dietary'])

  const filterErrors = showValidationErrors
    ? getConcessionFiltersAdminFieldErrors({
      locationSections,
      cuisineOptions,
      dietaryOptions
    })
    : {
      location: {
        category: '',
        items: locationSections.map(location => ({
          title: '',
          sections: (location.sections || []).map(() => ({ label: '' }))
        }))
      },
      cuisine: {
        category: '',
        options: cuisineOptions.map(() => ({ label: '', color: '' }))
      },
      dietary: {
        category: '',
        options: dietaryOptions.map(() => ({ label: '', color: '' }))
      }
    }

  useEffect(() => {
    initializePages(accessToken)
  }, [accessToken, initializePages])

  const loadFilters = useCallback(async venueId => {
    if (!venueId) return

    setLoading(true)
    try {
      const { method, url } = concessionFiltersEndpoint.get(venueId)
      const idToken = getIdToken()
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${idToken}` },
        method
      }).then(async res => await getApiResponseInJSON(res))

      const filters = response?.filters || {}
      const locationData = filters.locations || filters.location || DEFAULT_LOCATION
      setLocationSections(withLocationRowIds(locationData.sections))
      setLocationPlaceholder(locationData.placeholder || DEFAULT_LOCATION.placeholder)
      setCuisineOptions(withRowIds(filters.cuisine?.options))
      setCuisinePlaceholder(filters.cuisine?.placeholder || DEFAULT_CUISINE.placeholder)
      setDietaryOptions(withRowIds(filters.dietary?.options))
      setDietaryPlaceholder(filters.dietary?.placeholder || DEFAULT_DIETARY.placeholder)
    } catch (error) {
      notification.error({ message: error.message || 'Failed to load concession filters' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedVenueId) {
      loadFilters(selectedVenueId)
    }
  }, [selectedVenueId, loadFilters])

  const handleVenueChange = venueId => {
    setSelectedVenueId(venueId || '')
    setShowValidationErrors(false)
    if (!venueId) {
      setLocationSections([])
      setLocationPlaceholder(DEFAULT_LOCATION.placeholder)
      setCuisineOptions([])
      setCuisinePlaceholder(DEFAULT_CUISINE.placeholder)
      setDietaryOptions([])
      setDietaryPlaceholder(DEFAULT_DIETARY.placeholder)
    }
  }

  const handleSave = async () => {
    if (!isContentEditor) {
      notification.error({ message: 'You are not authorized to perform this action.' })
      return
    }
    if (!selectedVenueId) {
      notification.error({ message: 'Please select a venue.' })
      return
    }

    const validation = validateConcessionFiltersAdmin({
      locationSections,
      cuisineOptions,
      dietaryOptions
    })
    if (!validation.isValid) {
      setShowValidationErrors(true)
      const panelsWithErrors = []
      if (hasConcessionLocationCategoryFieldErrors(validation.fieldErrors.location)) {
        panelsWithErrors.push('location')
      }
      if (hasConcessionFilterCategoryFieldErrors(validation.fieldErrors.cuisine)) {
        panelsWithErrors.push('cuisine')
      }
      if (hasConcessionFilterCategoryFieldErrors(validation.fieldErrors.dietary)) {
        panelsWithErrors.push('dietary')
      }
      setActiveFilterPanels(panelsWithErrors.length ? panelsWithErrors : ['location', 'cuisine', 'dietary'])
      notification.error({ message: validation.message })
      return
    }

    const selectedVenue = venues?.find(v => String(v._id) === String(selectedVenueId))
    const payload = {
      venue: {
        name: selectedVenue?.name || '',
        slug: selectedVenue?.slug || ''
      },
      locations: {
        ...DEFAULT_LOCATION,
        placeholder: locationPlaceholder,
        venue_id: selectedVenueId,
        sections: locationSections.map(stripLocationItem)
      },
      cuisine: {
        ...DEFAULT_CUISINE,
        placeholder: cuisinePlaceholder,
        options: cuisineOptions.map(stripFilterItem)
      },
      dietary: {
        ...DEFAULT_DIETARY,
        placeholder: dietaryPlaceholder,
        options: dietaryOptions.map(stripFilterItem)
      }
    }

    setSaving(true)
    try {
      const { method, url } = concessionFiltersEndpoint.update(selectedVenueId)
      const idToken = getIdToken()
      const response = await fetch(url, {
        body: JSON.stringify(payload),
        headers: {
          Authorization: `Bearer ${idToken}`,
          AccessToken: accessToken,
          'Content-Type': 'application/json'
        },
        method
      }).then(async res => await getApiResponseInJSON(res))

      if (response?.success === false) {
        throw new Error(response?.message || 'Failed to save concession filters')
      }

      notification.success({ message: response?.message || 'Concession filters saved' })
      setShowValidationErrors(false)
      await loadFilters(selectedVenueId)
    } catch (error) {
      notification.error({ message: error.message || 'Failed to save concession filters' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Layout>
      <PageHeader
        extra={
          <Button loading={saving} onClick={handleSave} type="primary">
            Save
          </Button>
        }
        title={PAGE_TITLE}
      />
      <Layout.Content style={{ margin: 24 }}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Typography.Text strong>Venue</Typography.Text>
              <Select
                allowClear
                onChange={handleVenueChange}
                optionFilterProp="children"
                placeholder="Select a venue"
                showSearch
                style={{ width: '100%', marginTop: 8, maxWidth: 480 }}
                value={selectedVenueId || undefined}
              >
                {(venues || []).map(venue => (
                  <Select.Option key={venue._id} value={venue._id}>
                    {venue.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            {loading ? (
              <Spinner isLoading size="large" />
            ) : selectedVenueId ? (
              <Collapse activeKey={activeFilterPanels} onChange={setActiveFilterPanels}>
                <Panel key="location" header="LOCATION">
                  <FilterPlaceholderField
                    label="Placeholder"
                    onChange={setLocationPlaceholder}
                    value={locationPlaceholder}
                  />
                  <LocationFilterEditor
                    categoryError={filterErrors.location?.category}
                    itemErrors={filterErrors.location?.items || []}
                    items={locationSections}
                    onChange={setLocationSections}
                  />
                </Panel>
                <Panel key="cuisine" header="CUISINE">
                  <FilterPlaceholderField
                    label="Placeholder"
                    onChange={setCuisinePlaceholder}
                    value={cuisinePlaceholder}
                  />
                  <FilterOptionsTable
                    categoryError={filterErrors.cuisine?.category}
                    itemErrors={filterErrors.cuisine?.options || []}
                    items={cuisineOptions}
                    onChange={setCuisineOptions}
                    sectionLabel="Cuisine"
                  />
                </Panel>
                <Panel key="dietary" header="DIETARY PREFERENCES">
                  <FilterPlaceholderField
                    label="Placeholder"
                    onChange={setDietaryPlaceholder}
                    value={dietaryPlaceholder}
                  />
                  <FilterOptionsTable
                    categoryError={filterErrors.dietary?.category}
                    itemErrors={filterErrors.dietary?.options || []}
                    items={dietaryOptions}
                    onChange={setDietaryOptions}
                    sectionLabel="Dietary Preference"
                  />
                </Panel>
              </Collapse>
            ) : (
              <Typography.Text type="secondary">
                Select a venue to manage concession filter options.
              </Typography.Text>
            )}
          </Space>
        </Card>
      </Layout.Content>
    </Layout>
  )
}

const mapStateToProps = ({ pages }) => ({
  venues: pages.venues
})

const mapDispatchToProps = {
  initializePages
}

ConcessionFiltersAdmin.propTypes = {
  initializePages: PropTypes.func.isRequired,
  venues: PropTypes.array
}

export default connect(mapStateToProps, mapDispatchToProps)(ConcessionFiltersAdmin)
